// =============================================================================
// MeuDriver — TripLiveMap
// -----------------------------------------------------------------------------
// Mapa que o aluno vê enquanto espera: carro do instrutor + ponto de encontro.
//
// Só renderiza o carro quando há posição ao vivo. Fora da corrida, mostra
// apenas o ponto de encontro — sem mapa fantasma nem marcador em (0,0).
//
// Ver docs/mapa/modulos/rastreamento-ao-vivo.md
// =============================================================================

import { useEffect, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L, { type LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import type { LivePosition } from '@/hooks/useTripLive'

const PickupIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

/**
 * Ícone do carro em SVG inline, girado pelo heading.
 *
 * Inline (não arquivo) para não somar um request e para a rotação poder ser
 * aplicada sem CSS extra. `#0054b3` é a cor da marca.
 */
function carIcon(headingDeg: number | null): L.DivIcon {
  const rotation = headingDeg ?? 0
  return L.divIcon({
    className: 'trip-car-marker',
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    html: `
      <div style="transform: rotate(${rotation}deg); transition: transform .4s ease-out;">
        <svg width="38" height="38" viewBox="0 0 38 38" aria-label="Instrutor">
          <circle cx="19" cy="19" r="17" fill="#0054b3" stroke="#ffffff" stroke-width="3"/>
          <path d="M19 9 L25 26 L19 22 L13 26 Z" fill="#ffffff"/>
        </svg>
      </div>`,
  })
}

function Recenter({ points }: { points: LatLngExpression[] }) {
  const map = useMap()
  useEffect(() => {
    if (points.length === 0) return
    if (points.length === 1) {
      map.setView(points[0], 15)
      return
    }
    // Enquadra carro + destino, com folga para os marcadores não colarem na borda.
    map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 16 })
  }, [points, map])
  return null
}

interface Props {
  position: LivePosition | null
  pickupLat: number
  pickupLng: number
  pickupAddress?: string | null
  instructorName?: string | null
  height?: number | string
  className?: string
}

export function TripLiveMap({
  position,
  pickupLat,
  pickupLng,
  pickupAddress,
  instructorName,
  height = 320,
  className,
}: Props) {
  const pickup: LatLngExpression = useMemo(() => [pickupLat, pickupLng], [pickupLat, pickupLng])

  const points = useMemo<LatLngExpression[]>(
    () => (position ? [[position.lat, position.lng], pickup] : [pickup]),
    [position, pickup],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%', borderRadius: 12, overflow: 'hidden', position: 'relative' }}
    >
      <MapContainer
        center={pickup}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter points={points} />

        <Marker position={pickup} icon={PickupIcon}>
          <Popup>
            <strong>Ponto de encontro</strong>
            {pickupAddress ? <><br />{pickupAddress}</> : null}
          </Popup>
        </Marker>

        {position && (
          <Marker position={[position.lat, position.lng]} icon={carIcon(position.headingDeg)}>
            <Popup>{instructorName ?? 'Seu instrutor'}</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  )
}

export default TripLiveMap
